import { hasRealPrice } from 'utils/price';
import React, { useState, useEffect, useRef, useMemo, SyntheticEvent, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import './SendPage.css';
import SendPageHeader from './components/SendPageHeader';
import { SetInfo } from 'store/services/sets/setsApi';
import CardPrintingsContainer from 'components/CardPrintingsContainer/CardPrintingsContainer';
import CardGroupContainerResponsive from 'components/CardGroupContainer/CardGroupContainerResponsive';
import InitiateTradeModal, { TradeCard } from './components/InitiateTradeModal';
import {
  useGetAvailablePackagesQuery,
  useLazyGetAvailablePackagesQuery,
  useInitiateTradeMutation,
  TradePackage,
  TradeCard as ApiTradeCard,
  useCreateTradeLockMutation,
  useReleaseTradeLockMutation,
  tradeApi,
  useGetMatchIndexQuery,
  useTrackCsvExportMutation,
} from 'store/services/trade/tradeApi';
import { RootState } from 'store';
import { Points } from 'components/Points/Points';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import { meApi, useGetMeQuery } from 'store/services/user/meApi';
import { useHaveListQuantitySummaryQuery } from 'store/services/collectionList/haveList/haveListApi';
import { useCommitmentConflictCheck } from 'hooks/useCommitmentConflictCheck';
import CommitmentConflictDialog from 'components/CommitmentConflictDialog/CommitmentConflictDialog';
import { useResendVerificationMutation } from 'store/services/auth/authApi';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { getSetCode, isUUID } from 'utils/setMappings';
import { FoilCardImage } from 'components/FoilCardImage';
import {
  expandAutoMatchCards,
  resolveAutoMatchImageUrl,
  buildHaveMatchMapFromPackage,
  buildModalTradeCard,
  AutoMatchedCard,
  HaveMatchEntry,
  sendCardCopyKey,
  sendCardCopyKeys,
  buildSelectionAllExcept,
  filterCardsBySelection,
  chunkPriceRequest,
  mergeChunkedPrices,
} from './utils/autoMatchExpansion';
import SendCardTooltip from './components/SendCardTooltip';
import Pagination from 'components/Pagination/Pagination';
import DomesticTradesBanner from './components/DomesticTradesBanner';
import { SendListView } from './components/SendListView';
import type { ProcessedPackage } from './components/SendListView/SendListView';
import { generateExplodedCsvRows, formatCsvContent, downloadCsv, ROW_LIMIT } from './utils/csvExport';
import { trackEvent } from 'utils/analytics';
import { buildPrintingUrl } from 'utils/cardUrlUtils';
import { useTraderBalanceSocket } from 'hooks/useTraderBalanceSocket';
import { useTraderListCollapse } from 'hooks/useTraderListCollapse';
import { NewWantTag, TraderCollapse } from 'components/TraderListCollapse/TraderListCollapse';
import { isNewWant, orderForCollapsed } from 'utils/traderListCollapse';
import { finishFromRow, finishFields, HaveFinish, haveFinishFromRow } from 'utils/finishes';

const PLACEHOLDER_IMAGE = AssetUrls.search.imageComing;

// Coupled to the backend cap in magictrade-backend
// src/services/pricing/PriceValidationService.ts:59-62 - a request over this size is
// rejected with a 400. Changing one requires changing the other.
const CALCULATE_PRICE_BATCH_SIZE = 100;

// Helper function to clean malformed URLs from backend
const cleanImageUrl = (url: string | null | undefined): string => {
  if (!url) return AssetUrls.profile.defaultAvatar;

  // If URL contains the error pattern, strip it
  if (url.includes('{failed')) {
    const cleanUrl = url.split('{failed')[0].trim();
    return cleanUrl || AssetUrls.profile.defaultAvatar;
  }

  return url;
};

/**
 * "View card details" control for the /sendcards image views.
 *
 * A real anchor so middle-click and cmd-click open a new tab; plain click is same-tab.
 * stopPropagation (never preventDefault) keeps the tile's add/remove-from-send toggle
 * from firing while the href still drives navigation.
 *
 * Resolves the printing that is actually on screen: under Auto-Match the tile renders
 * the sender's matched printing rather than the want row's own, so the href follows the
 * same _autoMatchHaveCardId lookup resolveAutoMatchImageUrl uses. Falls back to the
 * card's own printing, and renders nothing when no printing can be identified.
 */
const SendViewCardLink = ({ card }: { card: AutoMatchedCard }) => {
  const detail = card._autoMatchHaveCardId && card.acceptableSetDetails
    ? card.acceptableSetDetails.find((d) => String(d.cardId) === String(card._autoMatchHaveCardId))
    : card.acceptableSetDetails?.find((d) => !!card.scryfallId && d.scryfallId === card.scryfallId);

  const href = buildPrintingUrl({
    name: card.name,
    setCode: detail?.code ?? card.setCode,
    // TradeCard itself carries no collectorNumber; acceptableSetDetails does, so a
    // matched printing yields the slug URL and an unmatched one the UUID URL.
    collectorNumber: detail?.collectorNumber,
    scryfallId: detail?.scryfallId ?? card.scryfallId,
    lang: card.language,
  });
  if (!href) return null;

  return (
    <a
      className="send-card-view-btn"
      href={href}
      title="View card details"
      aria-label="View card details"
      onClick={(e) => e.stopPropagation()}
    >
      <img src={AssetUrls.search.arrowDiagonal} width={16} height={16} alt="" />
    </a>
  );
};

export default function SendPage2() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<TradePackage | null>(null);
  const [displayMode, setDisplayMode] = useState<'comfortable' | 'compact' | 'list'>('comfortable');
  // Saved per account and shared with Activity. Render-only: toggling never refetches packages.
  const traderCollapse = useTraderListCollapse();
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [currentLockId, setCurrentLockId] = useState<string | null>(null);
  const [currentLockToken, setCurrentLockToken] = useState<string | null>(null);
  const [lockExpiresAt, setLockExpiresAt] = useState<string | null>(null);
  // Mirror of the held lock for the unmount release below. React state setters
  // are async and a cleanup closure would read a stale render's values, so the
  // live token/receiver are kept in a ref. An EMPTY ref means "no lock is held"
  // and is the signal the unmount release no-ops on.
  const heldLockRef = useRef<{ receiverId: number; lockToken: string } | null>(null);
  // Guards two close paths that overlap in time (concurrency, not sequence).
  const isReleasingLockRef = useRef(false);
  // Initialize search from URL params if present
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [searchType, setSearchType] = useState<'card' | 'user'>((searchParams.get('searchType') as 'card' | 'user') || 'card');
  // Pagination state
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [autoAdvanceCount, setAutoAdvanceCount] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  // Price filter state - initialize from URL params
  const [priceMin, setPriceMin] = useState<number | undefined>(() => {
    const param = searchParams.get('priceMin');
    return param ? parseInt(param, 10) : undefined;
  });
  const [priceMax, setPriceMax] = useState<number | undefined>(() => {
    const param = searchParams.get('priceMax');
    return param ? parseInt(param, 10) : undefined;
  });
  const [debouncedPriceMin, setDebouncedPriceMin] = useState<number | undefined>(priceMin);
  const [debouncedPriceMax, setDebouncedPriceMax] = useState<number | undefined>(priceMax);
  // Set filter state
  const [selectedSets, setSelectedSets] = useState<SetInfo[]>([]);
  // Sort state
  const [sortValue, setSortValue] = useState<string>('default');
  const sortParams = useMemo(() => {
    if (sortValue === 'default') return {};
    const [sort, direction] = sortValue.split('-');
    return { sort, direction: direction as 'asc' | 'desc' };
  }, [sortValue]);
  // Track user-initiated fetches (pagination/search) vs background polling
  const [isUserInitiatedFetch, setIsUserInitiatedFetch] = useState(false);
  // Track sort changes specifically — drives the full-page loader (vs the in-place pagination overlay)
  const [isSortChanging, setIsSortChanging] = useState(false);
  // Track selected cards per package (packageUserId -> Set of cardIds)
  const [selectedCardsPerPackage, setSelectedCardsPerPackage] = useState<Map<string, Set<string>>>(new Map());
  const [removedPackages, setRemovedPackages] = useState<Set<string>>(new Set());
  const [loadingMessage, setLoadingMessage] = useState('Loading available packages...');
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimer2Ref = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch current user once — used by multiple memos below (prefer-domestic sort, etc.)
  const { data: meData, refetch: refetchUser } = useGetMeQuery();

  // Latest rendered (post-auto-match-expansion) packages by userId. Selection
  // handlers must build and read selection state against THESE cards, never raw
  // data.packages — the two collections disagree on membership and copy keys
  // whenever auto-match is active (see sendCardCopyKey).
  const renderedPackagesRef = useRef<Map<number, ProcessedPackage>>(new Map());

  // Auto-Match State Management
  const [globalAutoMatch, setGlobalAutoMatch] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sendPage_globalAutoMatch') === 'true';
    } catch {
      return false;
    }
  });

  // Per-package auto-match states (session only - not persisted)
  const [packageAutoMatchStates, setPackageAutoMatchStates] = useState<Map<string, boolean>>(new Map());

  // Hover tooltip state
  const [hoveredCardData, setHoveredCardData] = useState<{
    cardId: string;
    position: { x: number; y: number };
    data: ApiTradeCard;
    username: string;
  } | null>(null);
  const [hoveredSetCode, setHoveredSetCode] = useState<string | null>(null);

  // Redux state for rate limiting and maintenance mode
  const isRateLimited = useSelector((state: RootState) => state.rateLimit.isRateLimited);
  const isMaintenanceMode = useSelector((state: RootState) => state.maintenance.isMaintenanceModeActive);

  // Calculate polling interval (pause if modal open, rate limited, or maintenance mode)
  const pollingInterval = (isModalOpen || isRateLimited || isMaintenanceMode) ? 0 : 15000;

  // Fetch match index for auto-match feature
  const {
    data: matchIndexData,
    isLoading: isMatchIndexLoading,
    refetch: refetchMatchIndex,
  } = useGetMatchIndexQuery();

  // Fetch sender's have list quantity summary for "You Own" column
  const { data: quantitySummaryData } = useHaveListQuantitySummaryQuery();
  const haveListQuantityByCardIdMap = useMemo(() => {
    const map = new Map<number, number>();
    if (quantitySummaryData?.quantitiesByCardId) {
      for (const [cardId, qty] of Object.entries(quantitySummaryData.quantitiesByCardId)) {
        map.set(Number(cardId), qty);
      }
    }
    return map;
  }, [quantitySummaryData]);

  // Commitment conflict check: a copy already promised to a pending trade offer
  // must not be async-sent too (same bug class as the offer builder, second
  // surface). The ref holds the attempted cards so the dialog can retry after
  // the user cancels the conflicting offer.
  const {
    conflicts: commitmentConflicts,
    clearConflicts: clearCommitmentConflicts,
    check: checkCommitmentConflicts,
    cancelConflictingOffers,
    isCancellingOffer,
  } = useCommitmentConflictCheck();
  const pendingTradeCardsRef = useRef<TradeCard[] | null>(null);

  // Server-side auto-match filtering when global auto-match is ON. The backend
  // derives the match set itself from the same MatchIndexService this page
  // reads, so we no longer enumerate user IDs into the URL (the old list grew
  // with the user base — 124+ IDs in production — and risked URL length
  // limits). On a match-index error we fall back to an unfiltered fetch and
  // let the client-side card filtering degrade gracefully, as before.
  const autoMatchServerFilter =
    globalAutoMatch && !!matchIndexData && !matchIndexData.error ? true : undefined;

  // Skip fetching packages until match index is ready when auto-match is on
  // This prevents fetching ALL users unfiltered during the brief window before match index loads
  const shouldSkipPackagesQuery = globalAutoMatch && isMatchIndexLoading;

  // Fetch available packages from the API
  const { data: rawData, isLoading, error, isFetching } = useGetAvailablePackagesQuery(
    {
      page: currentPage,
      limit: ITEMS_PER_PAGE,
      search: debouncedSearchTerm.trim() || undefined,
      searchType: debouncedSearchTerm.trim() ? searchType : undefined,
      priceMin: debouncedPriceMin,
      priceMax: debouncedPriceMax,
      autoMatch: autoMatchServerFilter,
      sort: sortParams.sort,
      direction: sortParams.direction,
      setCodes: selectedSets.length > 0 ? selectedSets.map(s => s.code).join(',') : undefined,
    },
    {
      pollingInterval, // Poll every 15 seconds (or pause if conditions met)
      skip: shouldSkipPackagesQuery,
    }
  );

  // Elite "Prefer Domestic Trades": re-sort so same-country traders appear first.
  // Within each partition, preserves the server's existing sort order.
  const data = useMemo(() => {
    if (!rawData?.packages) return rawData;
    const userCountry = meData?.country;
    if (!meData?.preferDomesticTrades || !userCountry) return rawData;
    const domestic: typeof rawData.packages = [];
    const international: typeof rawData.packages = [];
    for (const pkg of rawData.packages) {
      if (pkg.country === userCountry) {
        domestic.push(pkg);
      } else {
        international.push(pkg);
      }
    }
    if (domestic.length === 0) return rawData;
    return { ...rawData, packages: [...domestic, ...international] };
  }, [rawData, meData?.preferDomesticTrades, meData?.country]);

  // Debug authentication state on staging
  useEffect(() => {
    if (process.env.REACT_APP_ENV === 'staging') {
      console.log('🔐 Staging Auth Debug:', {
        isAuthenticated: !!localStorage.getItem('user'),
        hasCookies: document.cookie.length > 0,
        cookieList: document.cookie,
        apiUrl: process.env.REACT_APP_API_URL,
        cookieAuth: process.env.REACT_APP_USE_COOKIE_AUTH
      });

      if (error) {
        console.error('📦 Package fetch error:', error);
      }
    }
  }, [error]);

  // Subscribe to real-time balance updates for visible traders
  const traderIds = useMemo(
    () => Array.from(new Set((data?.packages || []).map(pkg => pkg.userId))),
    [data?.packages]
  );
  useTraderBalanceSocket({ traderIds, enabled: traderIds.length > 0 });

  // Keep selectedPackage.pointBalance in sync with fresh cache data
  useEffect(() => {
    if (data?.packages) {
      setSelectedPackage(prev => {
        if (!prev) return null;
        const freshPkg = data.packages.find(p => p.userId === prev.userId);
        if (freshPkg && freshPkg.pointBalance !== prev.pointBalance) {
          return { ...prev, pointBalance: freshPkg.pointBalance };
        }
        return prev;
      });
    }
  }, [data?.packages]);

  const [initiateTrade, { isLoading: isInitiatingTrade }] = useInitiateTradeMutation();
  const [createTradeLock] = useCreateTradeLockMutation();
  const [releaseTradeLock] = useReleaseTradeLockMutation();
  const [resendVerification] = useResendVerificationMutation();
  const [calculatePrice] = useCalculatePriceMutation();

  // Release a still-held trade lock when the page unmounts (route change, or a
  // browser back out of /sendcards with the modal open). This is the real leak:
  // without it the receiver stays locked and drops off /sendcards for the full
  // PACKAGE_LOCK_DURATION_SECONDS (300s) with nothing explaining why.
  //
  // Empty dep array on purpose: this must run ONLY on unmount. The values come
  // from heldLockRef, not from the render closure, so there is nothing to keep
  // in sync. handleCloseModal clears that ref synchronously before its await,
  // so a normal close leaves nothing here to release.
  useEffect(() => {
    return () => {
      const held = heldLockRef.current;
      if (!held) return; // already released by a close path
      heldLockRef.current = null;
      releaseTradeLock({ receiverId: held.receiverId, lockToken: held.lockToken })
        .unwrap()
        .catch(() => {
          // Silent: the lock expires on its own, and the component is gone so
          // there is no surface to report on. The backend answers a repeat
          // release with a 400 UNLOCK_ERROR, which is benign.
        });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State for auto-match printing-specific resolved prices
  const [autoMatchPrices, setAutoMatchPrices] = useState<Map<string, number | null>>(new Map());

  // CSV Export
  const [isExporting, setIsExporting] = useState(false);
  const [fetchAllPackages] = useLazyGetAvailablePackagesQuery();
  const [trackCsvExport] = useTrackCsvExportMutation();

  // We now use comprehensive static mappings from utils/setMappings.ts
  // No need to fetch from backend anymore

  // Only show loading state on initial load, not when refetching cached data
  // Also show loading when auto-match is on and we're waiting for match index
  // Also re-fire on sort change so users get the same full-page feedback as initial load
  const showLoadingState = (isLoading && !data) || shouldSkipPackagesQuery || isSortChanging;

  // Show loading overlay only during user-initiated pagination/search, not background polling
  const showPaginationLoading = isFetching && !!data && isUserInitiatedFetch;

  // Timer to update loading message progressively
  useEffect(() => {
    if (showLoadingState) {
      // Reset message when loading starts
      setLoadingMessage('Loading available packages...');

      // Set first timer to change message after 4 seconds
      loadingTimerRef.current = setTimeout(() => {
        setLoadingMessage('Building initial cache to improve future performance...');
      }, 4000);

      // Set second timer to change message after 10 seconds
      loadingTimer2Ref.current = setTimeout(() => {
        setLoadingMessage('Almost done...');
      }, 10000);
    } else {
      // Clear timers when loading stops
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      if (loadingTimer2Ref.current) {
        clearTimeout(loadingTimer2Ref.current);
        loadingTimer2Ref.current = null;
      }
      // Reset message for next load
      setLoadingMessage('Loading available packages...');
    }

    // Cleanup on unmount
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      if (loadingTimer2Ref.current) {
        clearTimeout(loadingTimer2Ref.current);
      }
    };
  }, [showLoadingState])

  // Persist global auto-match state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sendPage_globalAutoMatch', String(globalAutoMatch));
    } catch (error) {
      console.warn('Failed to persist auto-match state:', error);
    }
  }, [globalAutoMatch]);

  // Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== debouncedSearchTerm) {
        setIsUserInitiatedFetch(true); // Mark as user-initiated when search changes
      }
      setDebouncedSearchTerm(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearchTerm]);

  // Debounce price filters (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (priceMin !== debouncedPriceMin || priceMax !== debouncedPriceMax) {
        setIsUserInitiatedFetch(true);
      }
      setDebouncedPriceMin(priceMin);
      setDebouncedPriceMax(priceMax);
    }, 300);
    return () => clearTimeout(timer);
  }, [priceMin, priceMax, debouncedPriceMin, debouncedPriceMax]);

  // Sync filters to URL params
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (priceMin && priceMin > 0) newParams.set('priceMin', String(priceMin));
    else newParams.delete('priceMin');
    if (priceMax && priceMax > 0) newParams.set('priceMax', String(priceMax));
    else newParams.delete('priceMax');
    if (searchType !== 'card') newParams.set('searchType', searchType);
    else newParams.delete('searchType');
    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [priceMin, priceMax, searchType, searchParams, setSearchParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, debouncedPriceMin, debouncedPriceMax, autoMatchServerFilter, sortValue, searchType, selectedSets]);

  // Reset user-initiated fetch flag when fetching completes
  useEffect(() => {
    if (!isFetching && isUserInitiatedFetch) {
      setIsUserInitiatedFetch(false);
    }
  }, [isFetching, isUserInitiatedFetch]);

  // Reset sort-changing flag when fetch completes so the full-page loader clears
  useEffect(() => {
    if (!isFetching && isSortChanging) {
      setIsSortChanging(false);
    }
  }, [isFetching, isSortChanging]);

  // Sort change triggers the full-page loader (matches initial-load UX). isUserInitiatedFetch
  // is also flipped so the dropdown's spinner state in the loading-branch header still fires.
  const handleSortChange = useCallback((next: string) => {
    setSortValue(prev => {
      if (prev === next) return prev;
      setIsSortChanging(true);
      setIsUserInitiatedFetch(true);
      return next;
    });
  }, []);

  const isSortLoading = isFetching && !!data && isUserInitiatedFetch;

  // Auto-Match handler functions
  const handleGlobalAutoMatchToggle = (enabled: boolean) => {
    setGlobalAutoMatch(enabled);
  };

  const handlePackageAutoMatchToggle = (packageUserId: number, enabled: boolean) => {
    // Only allow when global is OFF
    if (globalAutoMatch) return;

    const packageKey = `pkg-${packageUserId}`;
    setPackageAutoMatchStates(prev => {
      const next = new Map(prev);
      next.set(packageKey, enabled);
      return next;
    });
  };

  // CSV Export handler
  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const result = await fetchAllPackages({
        page: 1,
        limit: 500,
        forExport: true,
        search: debouncedSearchTerm.trim() || undefined,
        priceMin: debouncedPriceMin,
        priceMax: debouncedPriceMax,
        autoMatch: autoMatchServerFilter,
      }).unwrap();

      if (!result.packages || result.packages.length === 0) {
        toast.info('No packages to export.');
        return;
      }

      const { rows, truncated } = generateExplodedCsvRows(result.packages);

      if (rows.length === 0) {
        toast.info('No exportable card data found in the filtered results.');
        return;
      }

      if (truncated) {
        toast.warn(
          `Export limited to ${ROW_LIMIT.toLocaleString()} rows. Consider narrowing your filters.`,
          { autoClose: 6000 }
        );
      }

      const csvContent = formatCsvContent(rows);
      const date = new Date().toISOString().split('T')[0];
      downloadCsv(csvContent, `trademagic-wants-${date}.csv`);

      trackEvent('csv_export', {
        total_packages: result.packages.length,
        total_rows: rows.length,
        was_truncated: truncated,
        had_search_filter: !!debouncedSearchTerm.trim(),
        had_price_filter: !!(debouncedPriceMin || debouncedPriceMax),
      });

      // Fire-and-forget backend analytics
      trackCsvExport().catch(() => {});

      toast.success(`Exported ${rows.length.toLocaleString()} rows to CSV.`);
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error('Failed to export CSV. Please try again.');
      trackEvent('csv_export_error', { error: String(err) });
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllPackages, trackCsvExport, debouncedSearchTerm, debouncedPriceMin, debouncedPriceMax, autoMatchServerFilter]);

  // Get effective auto-match state for a package
  const getPackageAutoMatchState = useCallback((packageUserId: number): boolean => {
    if (globalAutoMatch) return true;
    const packageKey = `pkg-${packageUserId}`;
    return packageAutoMatchStates.get(packageKey) ?? false;
  }, [globalAutoMatch, packageAutoMatchStates]);

  // Resolve sender's have-list image for a card when auto-match is active
  const getAutoMatchImageUrl = useCallback((
    card: ApiTradeCard,
    haveMatchMap?: Map<string, HaveMatchEntry>
  ): string | undefined => {
    return resolveAutoMatchImageUrl(card as AutoMatchedCard, haveMatchMap);
  }, []);

  // Build a map of wantListId → sender's have list match info from the match index
  // Used by getCurrentCards() to default set/condition/foil to the sender's actual card
  const buildHaveMatchMap = useCallback((userId: number): Map<string, HaveMatchEntry> => {
    if (!matchIndexData || matchIndexData.error) return new Map();
    const packageMatch = matchIndexData.packages?.[String(userId)];
    return buildHaveMatchMapFromPackage(packageMatch);
  }, [matchIndexData]);

  // Calculate match summary for header display
  const matchSummary = React.useMemo(() => {
    if (!matchIndexData || matchIndexData.error) return undefined;

    return {
      matchingCards: matchIndexData.totalMatchingCards,
      totalCards: data?.packages?.reduce((sum, pkg) => sum + pkg.cards.length, 0) || 0,
      matchingPackages: matchIndexData.totalMatchingPackages,
      totalPackages: data?.packages?.length || 0,
    };
  }, [matchIndexData, data?.packages]);

  const handleSendPackage = async (tradePackage: TradePackage) => {
    // Filter cards to only include selected ones
    const packageKey = `pkg-${tradePackage.userId}`;
    const selectedCards = selectedCardsPerPackage.get(packageKey);

    let filteredCards: ApiTradeCard[] = [];

    if (removedPackages.has(packageKey)) {
      // All cards removed - shouldn't send anything
      toast.warning('Please select at least one card to send');
      return;
    } else if (!selectedCards) {
      // No interaction with this package yet - default state, all cards selected.
      // (An EMPTY set is NOT the default state: it means the user deselected
      // every copy, and must reject below - matching what the grid renders.)
      filteredCards = tradePackage.cards;
    } else {
      // Some cards are selected - reduce to the selected copies using the same
      // canonical copy keys the grid renders and toggles with.
      filteredCards = filterCardsBySelection(tradePackage.cards, selectedCards);

      if (filteredCards.length === 0) {
        toast.warning('Please select at least one card to send');
        return;
      }
    }

    // Update the package with filtered cards
    const packageToSend = { ...tradePackage, cards: filteredCards };

    // Pre-flight check: Validate authentication before attempting lock
    try {
      console.log('🔐 Performing pre-flight authentication check...');
      const result = await refetchUser();
      if (result.error) {
        throw result.error;
      }
      console.log('✅ Authentication validated');

      // Check email verification status
      if (!result.data?.emailVerified) {
        console.log('❌ Email not verified');
        // Show toast with resend button
        toast.warning(
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Please verify your email address to send cards. Check your inbox for the verification link.</span>
            <button
              onClick={async () => {
                try {
                  if (!result.data?.email) {
                    toast.error('Unable to send verification email. Please try again.');
                    return;
                  }
                  await resendVerification({ email: result.data.email }).unwrap();
                  toast.success('Verification email sent! Please check your inbox.');
                } catch (error) {
                  toast.error('Failed to send verification email. Please try again.');
                }
              }}
              style={{
                padding: '4px 8px',
                background: '#7C3AED',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              Resend
            </button>
          </div>,
          {
            autoClose: false,
            closeButton: true
          }
        );
        return;
      }
    } catch (authError) {
      console.error('❌ Pre-flight authentication check failed:', authError);
      toast.error('Please refresh the page to restore your session');
      // Force refresh user data
      dispatch(meApi.util.invalidateTags(['User']));
      return;
    }

    // Retry logic for cookie authentication issues
    let retryCount = 0;
    const maxRetries = 2;
    const retryDelay = 500; // ms

    while (retryCount <= maxRetries) {
      try {
        // Log attempt for debugging
        if (retryCount > 0) {
          console.log(`🔄 Retry attempt ${retryCount} for trade lock creation`);
        }

        // Try to create a lock for this receiver
        const lockResult = await createTradeLock({ receiverId: tradePackage.userId }).unwrap();

        if (lockResult.success) {
          setCurrentLockId(lockResult.lockId || null);
          setCurrentLockToken(lockResult.lockToken || null);
          setLockExpiresAt(lockResult.expiresAt || null);
          // Record the held lock synchronously so an unmount before the state
          // setters flush still has what it needs to release.
          heldLockRef.current = lockResult.lockToken
            ? { receiverId: tradePackage.userId, lockToken: lockResult.lockToken }
            : null;
          setSelectedPackage(packageToSend); // Use the filtered package
          setIsModalOpen(true);
          return; // Success - exit function
        } else {
          // Someone else has locked this receiver
          toast.error(lockResult.message || 'Another sender is building a trade for this receiver. Please check back soon.');
          return; // Exit - no retry needed for business logic errors
        }
      } catch (error: any) {
        console.error(`Failed to create trade lock (attempt ${retryCount + 1}):`, error);
        console.error('Lock creation error details:', {
          status: error.status,
          message: error.data?.message,
          error: error.data?.error,
          details: error.data?.details,
          receiverId: tradePackage.userId,
          attempt: retryCount + 1
        });

        // Blocked pair: the lock endpoint rejects with a generic coded 403.
        // Must be checked before the 401/403 session branch below so it never
        // enters the session-expired retry loop.
        if (error.status === 403 && error.data?.code === 'TRADE_NOT_ALLOWED') {
          toast.error("You can't start this trade with this user.");
          return;
        }

        // The receiver turned Vacation Mode on after this page loaded. Checked before
        // the generic 409 branch below, which would call it "another package open".
        // This endpoint returns its text in `error`, not `message`.
        if (error.data?.code === 'VACATION_MODE') {
          toast.error(error.data?.error || error.data?.message || 'This trader is away right now.');
          return;
        }

        // Handle case where lock endpoint returns an error
        if (error.status === 423 || error.data?.message?.includes('already locked')) {
          toast.error('This package is temporarily unavailable. Another sender is currently building a trade for this receiver. Please try again in a few minutes.');
          return; // Exit - no retry for business logic errors
        } else if (error.status === 409 || error.data?.message?.includes('already have a lock')) {
          toast.error('You already have another package open for trading. Please complete or cancel your current trade first.');
          return; // Exit - no retry for business logic errors
        } else if (error.status === 401 || error.status === 403) {
          // Authentication issue - likely cookie not set after signup or expired
          console.error('Authentication failed when creating lock - cookie issue detected');

          if (retryCount < maxRetries) {
            // Wait and retry - cookie might need time to propagate
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            retryCount++;
            continue; // Retry the request
          } else {
            // All retries exhausted
            toast.error('Your session has expired. Please refresh the page to continue trading.');

            // Force a user data refetch to potentially refresh the session
            dispatch(meApi.util.invalidateTags(['User']));
            return;
          }
        } else if (error.status === 400) {
          // 400 can mean various issues - parse the backend message
          let errorMessage = 'Unable to reserve this package. ';

          const backendMessage = error.data?.message || error.data?.error || '';
          if (backendMessage.includes('already have a lock') || backendMessage.includes('existing lock')) {
            errorMessage = 'You already have another package open for trading. Please complete or cancel your current trade first.';
          } else if (backendMessage.includes('trade limit')) {
            errorMessage = 'The receiver has reached their maximum number of active trades. Please try again later.';
          } else if (backendMessage.includes('package') && backendMessage.includes('not found')) {
            errorMessage = 'This package is no longer available. It may have been traded or removed.';
          } else if (backendMessage) {
            errorMessage = backendMessage; // Use backend message if specific
          } else {
            errorMessage += 'The package may no longer be available.';
          }

          toast.error(errorMessage);
          return; // Exit - no retry for bad requests
        } else if (error.status === 500) {
          // Server error
          toast.error('The trade service is temporarily unavailable. Please try again in a moment.');
          return;
        } else if (error.status === 0 || error.code === 'ERR_NETWORK') {
          // Network error
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            retryCount++;
            continue;
          } else {
            toast.error('Unable to connect to the trade server. Please check your internet connection.');
            return;
          }
        } else {
          // Other errors - retry might help
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            retryCount++;
            continue;
          } else {
            // Lock is required - do not proceed without it
            toast.error('Unable to reserve this package for trading. Please try again or contact support if the problem persists.');
            return;
          }
        }
      }
    }
  };

  const handleCloseModal = async () => {
    // Claim the lock SYNCHRONOUSLY, before any await. Clearing the ref up front
    // is what makes the release idempotent on the client: the unmount cleanup
    // below no-ops on an empty ref, so a close followed by an unmount fires
    // exactly one DELETE. Without this the state setters at the bottom are
    // async, the modal is conditionally rendered, and an ordinary close would
    // be followed by a cleanup that still saw a token and fired a second
    // DELETE (which the backend answers with a 400 UNLOCK_ERROR).
    const held = heldLockRef.current;
    heldLockRef.current = null;

    // Release the lock if we have one. isReleasingLockRef guards the separate
    // case of two close paths overlapping in flight; the ref clear above
    // handles the sequential case.
    if (held && !isReleasingLockRef.current) {
      isReleasingLockRef.current = true;
      try {
        await releaseTradeLock({
          receiverId: held.receiverId,
          lockToken: held.lockToken
        }).unwrap();

        // Force refresh of available packages to show the unlocked package
        dispatch(tradeApi.util.invalidateTags(['TradePackages']));
      } catch (error: any) {
        console.error('Failed to release trade lock:', error);
        // Silent failure - lock will expire automatically after 5 minutes
      } finally {
        isReleasingLockRef.current = false;
      }
    }

    // Clear state regardless of lock release success
    setIsModalOpen(false);
    setSelectedPackage(null);
    setCurrentLockId(null);
    setCurrentLockToken(null);
    setLockExpiresAt(null);
  };

  const handleInitiateTrade = async (cards: TradeCard[]) => {
    if (!selectedPackage) {
      toast.error('No package selected');
      return;
    }

    // Validate that no cards have 0 value
    const hasZeroValueCards = cards.some(card => {
      const cardValue = card.value || 0;
      return cardValue <= 0;
    });

    if (hasZeroValueCards) {
      toast.error('Cannot initiate trade: One or more cards have 0 points. Please remove cards with no value.');
      return;
    }

    // Calculate total value to ensure it's not 0
    const totalValue = cards.reduce((sum, card) => sum + (card.value || 0), 0);

    if (totalValue <= 0) {
      toast.error('Cannot initiate trade: Total trade value must be greater than 0 points.');
      return;
    }

    // All price validation happens server-side
    // Frontend prices are for display only

    try {
      // Map the cards from modal format to API format
      // Each row is an individual copy with quantity: 1
      const apiCards = cards.map(card => {
        // Look up original package card by wantListId (card.id is now a unique row ID).
        // For auto-match collapsed cards, the modal row's wantListId comes from one
        // of the card's _autoMatchAssignments and may not equal the collapsed card's
        // representative wantListId — fall through to assignment lookup in that case.
        const originalCard =
          selectedPackage.cards.find(c => c.wantListId === card.wantListId)
          ?? selectedPackage.cards.find(c =>
            (c as AutoMatchedCard)._autoMatchAssignments?.some(a => a.wantListId === card.wantListId)
          );
        if (!originalCard) {
          throw new Error(`Card ${card.name} not found in package`);
        }

        // Debug logging for cardId tracking
        console.log(`Card ${card.name} - Set: ${card.set}`, {
          originalCardId: originalCard.cardId,
          modalCardId: (card as any).cardId,
          selectedSetCode: card.setCode,
          wantListId: originalCard.wantListId,
          acceptedPrintingIds: (originalCard as any).acceptedPrintingIds
        });

        // Use the cardId from the modal if it was updated (when user changed set)
        // Otherwise fall back to the original cardId
        const finalCardId = card.cardId || originalCard.cardId;

        return {
          wantListId: originalCard.wantListId,
          cardId: finalCardId?.toString(),  // Include specific card ID for exact version matching
          quantity: 1,  // Each row is an individual copy
          condition: card.condition,
          language: card.language,
          // finish is authoritative; the derived foil rides along through Track D
          ...finishFields(haveFinishFromRow(card)),
          edition: card.edition || undefined,
          set: card.setCode,  // Use setCode for API calls, not set name
        };
      });

      // Debug log the payload being sent
      console.log('Trade initiation payload:', {
        receiverId: selectedPackage.userId,
        cards: apiCards.map(c => ({ ...c, cardName: cards.find(card => card.id === selectedPackage.cards.find(oc => oc.wantListId === c.wantListId)?.id)?.name }))
      });

      // Interrupt on a real shortfall (committed to pending offers + this send
      // exceeds owned) before the trade is created; spare copies never fire it.
      pendingTradeCardsRef.current = cards;
      const clearToProceed = await checkCommitmentConflicts(
        apiCards.map((apiCard, idx) => ({
          cardId: apiCard.cardId,
          name: cards[idx]?.name || 'This card',
        }))
      );
      if (!clearToProceed) return;

      const result = await initiateTrade({
        receiverId: selectedPackage.userId,
        lockToken: currentLockToken || undefined,
        source: 'send-cards',
        cards: apiCards,
      }).unwrap();

      if (result.success) {
        toast.success('Trade initiated successfully!');

        // Clear lock state immediately - backend auto-releases lock on successful trade
        // This prevents handleCloseModal from attempting a redundant release
        // (and the ref clear stops the unmount cleanup below from releasing a
        // lock the backend already dropped when we navigate to /sending).
        heldLockRef.current = null;
        setCurrentLockToken(null);
        setCurrentLockId(null);
        setLockExpiresAt(null);

        // Force refresh packages after successful trade
        dispatch(tradeApi.util.invalidateTags(['TradePackages']));

        // Close modal and clear remaining state
        setIsModalOpen(false);
        setSelectedPackage(null);

        // Redirect to /sending page to see the new trade
        navigate('/sending');
      } else {
        toast.error(result.message || 'Failed to initiate trade');
      }
    } catch (error: any) {
      console.error('Failed to initiate trade:', error);
      console.error('Trade initiation error details:', {
        status: error.status,
        message: error.data?.message,
        error: error.data?.error,
        details: error.data?.details,
        cards: cards.map(c => ({ name: c.name, qty: c.qty, set: c.set })),
        receiverId: selectedPackage.userId
      });

      // Blocked pair: generic coded 403 — show a generic message, nothing more
      if (error.status === 403 && error.data?.code === 'TRADE_NOT_ALLOWED') {
        toast.error("You can't start this trade with this user.");
        return;
      }

      // Handle specific error cases from backend
      const errorMessage = error.data?.message || error.data?.error || error.message || '';

      // The receiver cannot afford the package. The backend's code is the signal;
      // the case-insensitive text match covers a backend that does not send it yet
      // (its message reads "Receiver has insufficient points. Required: X,
      // Available: Y", which the old case-sensitive 'Insufficient points' check
      // never matched). The balance may have moved since the modal opened, so
      // refetch the packages: the open modal re-reads the receiver's balance and
      // recomputes the shortfall itself. No number is parsed out of the message.
      if (
        error.data?.code === 'INSUFFICIENT_RECEIVER_BALANCE' ||
        /insufficient points/i.test(errorMessage)
      ) {
        toast.error(errorMessage || "The receiver doesn't have enough points to complete this trade.");
        dispatch(tradeApi.util.invalidateTags(['TradePackages']));
        return;
      }

      // Parse error message for specific scenarios
      if (errorMessage.includes('not found in want list') || errorMessage.includes('no longer wanted')) {
        // Extract card name if available
        const cardMatch = errorMessage.match(/card "([^"]+)"/i) || errorMessage.match(/(\w+) (?:is |are )?not found/i);
        if (cardMatch) {
          toast.error(`"${cardMatch[1]}" is no longer in the receiver's want list. The package has been updated.`);
        } else {
          toast.error('Some cards are no longer in the receiver\'s want list. The package has been updated - please try again.');
        }
      } else if (errorMessage.includes('quantity exceeds') || errorMessage.includes('too many copies')) {
        // Extract card and quantity info
        const quantityMatch = errorMessage.match(/only wants? (\d+) (?:copies?|cards?).*?(?:of |for )?["']?([^"']+)["']?/i);
        if (quantityMatch) {
          toast.error(`The receiver only wants ${quantityMatch[1]} copies of "${quantityMatch[2]}". Please reduce the quantity.`);
        } else if (errorMessage.includes('already sent') || errorMessage.includes('already received')) {
          // Handle case where user already sent some copies
          const sentMatch = errorMessage.match(/already (?:sent|received) (\d+).*?wants? (\d+)/i);
          if (sentMatch) {
            toast.error(`You've already sent ${sentMatch[1]} copies to this user. They only want ${sentMatch[2]} total copies.`);
          } else {
            toast.error('You\'ve already sent some copies of this card to this user. They don\'t need more.');
          }
        } else {
          toast.error('The quantity exceeds what the receiver wants. Please check their want list.');
        }
      } else if (errorMessage.includes('shipping address') || errorMessage.includes('address required')) {
        toast.error('You must add a shipping address in Settings before initiating trades.');
      } else if (errorMessage.includes('User not found') || errorMessage.includes('user does not exist')) {
        toast.error('Unable to verify user information. Please log out and log back in.');
      } else if (errorMessage.includes('lock expired') || errorMessage.includes('lock invalid') || errorMessage.includes('Valid lock required')) {
        toast.error('Your package reservation has expired (5 minute limit). Please select the package again.');
      } else if (errorMessage.includes('not in acceptable sets') || errorMessage.includes('set not accepted')) {
        // Extract set and card info
        const setMatch = errorMessage.match(/(?:card |)["']?([^"']+)["']?.*?from (?:the |)["']?([^"']+)["']? set/i);
        if (setMatch) {
          toast.error(`The receiver doesn't accept "${setMatch[1]}" from the "${setMatch[2]}" set. Please choose a different version.`);
        } else {
          toast.error('The selected card version is not accepted by the receiver. Please choose a different set.');
        }
      } else if (errorMessage.includes('condition not acceptable') || errorMessage.includes('better condition')) {
        // Extract condition requirements
        const conditionMatch = errorMessage.match(/requires? (?:at least |minimum |)(\w+)/i);
        if (conditionMatch) {
          toast.error(`The receiver requires better condition for this card. Minimum condition: ${conditionMatch[1]}.`);
        } else {
          toast.error('The receiver requires better condition for this card.');
        }
      } else if (errorMessage.includes('duplicate trade') || errorMessage.includes('already initiated')) {
        toast.error('You\'ve already initiated a similar trade with this user. Please wait for it to complete before sending more.');
      } else if (errorMessage.includes('max trades') || errorMessage.includes('trade limit')) {
        toast.error('You\'ve reached the maximum number of active trades. Please complete some trades before starting new ones.');
      } else if (error.status === 500) {
        toast.error('The trade service encountered an error. Please try again in a moment.');
      } else if (error.status === 503) {
        toast.error('The trade service is temporarily unavailable. Please try again later.');
      } else if (error.status === 0 || error.code === 'ERR_NETWORK') {
        toast.error('Unable to connect to the trade server. Please check your internet connection.');
      } else if (errorMessage) {
        // Use backend error message if it's specific and not already handled
        toast.error(errorMessage);
      } else {
        // Generic fallback
        toast.error('Failed to initiate trade. Please try again or contact support if the problem persists.');
      }
    }
  };

  // Handle toggling individual card selection
  const handleToggleCard = (packageUserId: number, cardId: string) => {
    const packageKey = `pkg-${packageUserId}`;
    const currentSelected = selectedCardsPerPackage.get(packageKey);
    const isPackageRemoved = removedPackages.has(packageKey);
    let newSelected: Set<string>;

    if (currentSelected === undefined) {
      // First interaction with this package
      if (isPackageRemoved) {
        // First click after Remove All - select only this card
        newSelected = new Set<string>([cardId]);
        // Remove from removed packages since user is re-selecting
        const newRemoved = new Set(removedPackages);
        newRemoved.delete(packageKey);
        setRemovedPackages(newRemoved);
      } else {
        // Normal first click - select all rendered copies except the clicked one.
        // Built from the RENDERED cards so the keys match what the grid toggles
        // and what handleSendPackage filters with.
        const rendered = renderedPackagesRef.current.get(packageUserId);
        newSelected = buildSelectionAllExcept(rendered?.valuedCards ?? [], cardId);
      }
    } else {
      // Already interacted with package - just toggle the card
      newSelected = new Set(currentSelected);
      if (newSelected.has(cardId)) {
        newSelected.delete(cardId);
      } else {
        newSelected.add(cardId);
      }
    }

    const newMap = new Map(selectedCardsPerPackage);
    newMap.set(packageKey, newSelected);
    setSelectedCardsPerPackage(newMap);
  };

  // Handle card hover for tooltip display
  const handleCardMouseEnter = (
    e: React.MouseEvent<HTMLElement>,
    card: ApiTradeCard,
    username: string
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    // For card grid items: account for hover transform scale(1.5) adding 25% to right side
    // For list view rows: no scale, use right edge directly
    const isGridCard = e.currentTarget.classList.contains('card-item');
    const rightEdge = isGridCard ? rect.right + (rect.width * 0.25) : rect.right + 8;
    setHoveredCardData({
      cardId: card.id,
      position: { x: rightEdge, y: rect.top },
      data: card,
      username,
    });
  };

  const handleCardMouseLeave = () => {
    setHoveredCardData(null);
    setHoveredSetCode(null);
  };

  // Calculate pagination values
  // When auto-match is on, use match index count (accurate count of matching users)
  // instead of API's totalPackages (which may include non-matching users)
  const totalPages = useMemo(() => {
    if (globalAutoMatch && matchIndexData && !matchIndexData.error) {
      const matchCount = matchIndexData.totalMatchingPackages || 0;
      return matchCount > 0 ? Math.ceil(matchCount / ITEMS_PER_PAGE) : 1;
    }
    return data?.totalPackages
      ? Math.ceil(data.totalPackages / ITEMS_PER_PAGE)
      : 1;
  }, [globalAutoMatch, matchIndexData, data?.totalPackages]);

  // Page change handler
  const handlePageChange = useCallback((newPage: number) => {
    setIsUserInitiatedFetch(true); // Mark as user-initiated when page changes
    setCurrentPage(newPage);
    // Scroll both containers to top (matches ScrollToTop pattern)
    const mainContent = document.querySelector('.main-page-content');
    if (mainContent) {
      mainContent.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  }, []);

  // Handle toggling all instances of a card (compact mode tiles + list view rows).
  // Takes the rendered card object - not a bare id - because in auto-match mode
  // the canonical copy key comes from _autoMatchCompositeId, not card.id.
  const handleToggleAllCardInstances = (packageUserId: number, card: ApiTradeCard) => {
    const packageKey = `pkg-${packageUserId}`;
    const currentSelected = selectedCardsPerPackage.get(packageKey);
    const isPackageRemoved = removedPackages.has(packageKey);
    const cardKeys = sendCardCopyKeys(card);

    // Check current selection state of all instances
    let selectedCount = 0;
    if (isPackageRemoved) {
      selectedCount = 0;
    } else if (currentSelected === undefined) {
      // No interaction yet - default to all selected
      selectedCount = cardKeys.length;
    } else {
      selectedCount = cardKeys.filter(key => currentSelected.has(key)).length;
    }

    // Determine new selection state
    let newSelected: Set<string>;
    if (currentSelected === undefined) {
      // First interaction with this package
      if (isPackageRemoved) {
        // Package was removed (Remove All was clicked)
        // Only select the clicked card instances
        newSelected = new Set<string>(cardKeys);
        // Clear removed packages flag since user is re-selecting
        const newRemoved = new Set(removedPackages);
        newRemoved.delete(packageKey);
        setRemovedPackages(newRemoved);
      } else {
        // Normal first interaction - select every rendered copy except the
        // clicked card's copies, in the canonical key space.
        const rendered = renderedPackagesRef.current.get(packageUserId);
        newSelected = buildSelectionAllExcept(rendered?.valuedCards ?? [], new Set(cardKeys));
      }
    } else {
      newSelected = new Set(currentSelected);

      // Toggle: if all selected -> deselect all; otherwise -> select all
      if (selectedCount === cardKeys.length) {
        cardKeys.forEach(key => newSelected.delete(key));
      } else {
        cardKeys.forEach(key => newSelected.add(key));
      }
    }

    const newMap = new Map(selectedCardsPerPackage);
    newMap.set(packageKey, newSelected);
    setSelectedCardsPerPackage(newMap);
  };

  // Handle removing all cards from a package
  const handleRemoveAllCards = (packageUserId: number) => {
    const packageKey = `pkg-${packageUserId}`;
    const newMap = new Map(selectedCardsPerPackage);
    newMap.delete(packageKey); // Clear all selections for this package
    setSelectedCardsPerPackage(newMap);

    // Add to removed packages set
    const newRemoved = new Set(removedPackages);
    newRemoved.add(packageKey);
    setRemovedPackages(newRemoved);
  };

  // Handle resetting a package (re-selecting all cards). Deleting the selection
  // entry returns the package to the default state - "no interaction, everything
  // selected" - which is key-space-proof: it stays correct even if the rendered
  // card set changes (e.g. auto-match toggles) between reset and send.
  const handleResetPackage = (packageUserId: number) => {
    const packageKey = `pkg-${packageUserId}`;

    const newMap = new Map(selectedCardsPerPackage);
    newMap.delete(packageKey);
    setSelectedCardsPerPackage(newMap);

    // Remove from removed packages set
    const newRemoved = new Set(removedPackages);
    newRemoved.delete(packageKey);
    setRemovedPackages(newRemoved);
  };

  const handleImageError = (e: SyntheticEvent<HTMLImageElement>, card: any) => {
    const img = e.currentTarget;
    const currentSrc = img.src;

    // Prevent infinite loops - check if we've already tried placeholder
    if (currentSrc.includes('image-coming-soon.png')) {
      img.onerror = null;
      return;
    }

    // First try backend-provided fallback URLs if available
    const fallbacks = card?.normal_fallbacks;
    if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
      img.dataset.fallbackAttempted = 'true';
      img.src = fallbacks[0];
      return;
    }

    // Try to extract Scryfall ID from CDN URL to attempt different formats
    const cdnMatch = currentSrc.match(/\/cards\/(normal|small|large)\/([a-f0-9-]+)\.jpg/);

    if (cdnMatch && !img.dataset.formatFallbackComplete) {
      const currentFormat = cdnMatch[1];
      const scryfallId = cdnMatch[2];

      // Try fallback formats: normal -> small -> large -> placeholder
      if (currentFormat === 'normal') {
        img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
        return;
      } else if (currentFormat === 'small') {
        img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
        return;
      } else if (currentFormat === 'large') {
        img.dataset.formatFallbackComplete = 'true';
      }
    }

    // If all fallbacks failed, use placeholder
    img.src = PLACEHOLDER_IMAGE;
    img.onerror = null;
    setFailedImages(prev => new Set(prev).add(card.id));
  };


  const getCurrentCards = (): TradeCard[] => {
    if (!selectedPackage) return [];

    // When auto-match is active, build a map of wantListId → sender's have list info
    // so we can default set/condition/foil to the sender's actual card
    const isAutoMatch = getPackageAutoMatchState(selectedPackage.userId);
    const haveMatchMap = isAutoMatch ? buildHaveMatchMap(selectedPackage.userId) : new Map();
    const matchPackage = matchIndexData?.packages?.[String(selectedPackage.userId)];

    // Convert API cards to TradeCard format for the modal
    return selectedPackage.cards.flatMap(card => {
      // Auto-match collapsed cards carry a list of per-copy assignments — splatter
      // each into its own modal row so the modal naturally renders one row per
      // sendable copy with the correct wantListId. This guarantees the
      // InitiateTradeRequest emits one row per assignment and routes each copy
      // to the want it was matched against.
      const assignments = (card as AutoMatchedCard)._autoMatchAssignments;
      if (assignments && assignments.length > 0) {
        return assignments.map(a => buildModalTradeCard(
          {
            ...card,
            quantity: 1,
            wantListId: a.wantListId,
            ...finishFields(a.finish),
            _autoMatchHaveCardId: String(a.cardId),
            _autoMatchCompositeId: a.compositeId,
          } as AutoMatchedCard,
          { haveCardId: String(a.cardId), condition: a.condition, foil: a.foil, finish: a.finish },
          getSetCode,
          isUUID,
        ));
      }

      // v7: Auto-match - use _autoMatchHaveCardId (from expansion) if available,
      // otherwise fall back to haveMatchMap for non-expanded cards
      const storedHaveCardId = (card as any)._autoMatchHaveCardId as string | undefined;
      const storedCompositeId = (card as any)._autoMatchCompositeId as string | undefined;

      const haveMatch: HaveMatchEntry | undefined = storedHaveCardId
        ? {
            haveCardId: storedHaveCardId,
            condition: storedCompositeId ? matchPackage?.matchDetails?.[storedCompositeId]?.condition : undefined,
            foil: storedCompositeId ? matchPackage?.matchDetails?.[storedCompositeId]?.foil : undefined,
            finish: storedCompositeId ? matchPackage?.matchDetails?.[storedCompositeId]?.finish : undefined,
          }
        : haveMatchMap.get(String(card.wantListId));

      return [buildModalTradeCard(
        card as AutoMatchedCard,
        haveMatch,
        getSetCode,
        isUUID,
      )];
    });
  };

  // Filter out locked packages (backend handles shipping address validation)
  const packagesWithValidAddresses = useMemo(() => {
    return data?.packages?.filter(pkg => {
      if (!pkg) return false;
      // Keep our own locked package visible
      if (pkg.isLocked && currentLockToken && selectedPackage && selectedPackage.userId === pkg.userId) {
        return true;
      }
      // Skip packages locked by others
      if (pkg.isLocked) {
        return false;
      }
      return true;
    }) || [];
  }, [data?.packages, currentLockToken, selectedPackage]);

  // Pre-process packages for both card-grid and list views
  const processedPackages: ProcessedPackage[] = useMemo(() => {
    return (packagesWithValidAddresses || []).map((pkg) => {
      // Filter out cards with 0 quantity or 0 value
      let valuedCards = pkg.cards.filter(card => {
        if (card.quantity <= 0) return false;
        const cardValue = Math.max(
          card.pointValue || 0,
          card.foilPointValue || 0,
          card.nonFoilPointValue || 0
        );
        return cardValue > 0;
      });

      const packageKey = `pkg-${pkg.userId}`;
      const isPackageRemoved = removedPackages.has(packageKey);
      const isAutoMatchActive = getPackageAutoMatchState(pkg.userId);
      const packageMatch = matchIndexData?.packages?.[String(pkg.userId)];
      const totalCardsBeforeAutoMatch = valuedCards.length;

      const hasMatchIndexData = matchIndexData && !matchIndexData.error;
      if (isAutoMatchActive && hasMatchIndexData) {
        const compositeIds = packageMatch?.matchingCardIds || [];
        valuedCards = expandAutoMatchCards(
          valuedCards,
          compositeIds,
          packageMatch?.matchDetails,
          haveListQuantityByCardIdMap,
        );
      }

      // Number of physical cards sender can fulfill (deduplicated by the
      // backend per (oracleId, foil) group). matchingCardIds.length counts
      // (Want × Have) match pairs, so a single Have matching multiple Wants
      // inflates the count ("4 of 2 cards match" for 2 haves × 2 wants).
      // Fall back to matchingCardIds.length for older servers that don't
      // send matchedCardCount yet.
      const matchingCardCount = packageMatch?.matchedCardCount
        ?? packageMatch?.matchingCardIds.length
        ?? 0;
      const totalCardCount = packageMatch?.totalCards ?? totalCardsBeforeAutoMatch;

      const isEmptyDueToAutoMatch = isAutoMatchActive && !!hasMatchIndexData && valuedCards.length === 0 && totalCardsBeforeAutoMatch > 0;

      const haveMatchMap = isAutoMatchActive && hasMatchIndexData
        ? buildHaveMatchMap(pkg.userId)
        : undefined;

      return {
        pkg,
        valuedCards,
        packageKey,
        isPackageRemoved,
        isAutoMatchActive,
        isEmptyDueToAutoMatch,
        matchingCardCount,
        totalCardCount,
        haveMatchMap,
      };
    }).filter(processed => {
      // When GLOBAL auto-match is on and no cards match, hide the package entirely
      if (globalAutoMatch && processed.isEmptyDueToAutoMatch) return false;
      // Skip packages with no valued cards (unless empty due to individual auto-match)
      if (processed.valuedCards.length === 0 && !processed.isEmptyDueToAutoMatch) return false;
      return true;
    });
  }, [packagesWithValidAddresses, removedPackages, globalAutoMatch, matchIndexData, getPackageAutoMatchState, buildHaveMatchMap, haveListQuantityByCardIdMap]);

  // Collect auto-match expanded rows that need printing-specific price resolution
  // Build a stable key to avoid re-fetching on every render
  const autoMatchPriceRequestKey = useMemo(() => {
    const entries: Array<{ wantListId: number; haveCardId: string; compositeId: string; condition: string; language: string; finish: HaveFinish }> = [];
    for (const processed of processedPackages) {
      if (!processed.isAutoMatchActive) continue;
      for (const card of processed.valuedCards) {
        const haveCardId = (card as any)._autoMatchHaveCardId as string | undefined;
        const compositeId = (card as any)._autoMatchCompositeId as string | undefined;
        if (!haveCardId || !compositeId) continue;
        const matchPackage = matchIndexData?.packages?.[String(processed.pkg.userId)];
        const details = matchPackage?.matchDetails?.[compositeId];
        entries.push({
          wantListId: card.wantListId,
          haveCardId,
          compositeId,
          condition: details?.condition || card.condition || 'NM',
          language: card.language || 'English',
          // the matched have row's finish (finish first, foil as the legacy fallback)
          finish: details ? haveFinishFromRow(details) : haveFinishFromRow(card),
        });
      }
    }
    return entries;
  }, [processedPackages, matchIndexData]);

  // Fetch printing-specific prices for auto-match expanded rows
  useEffect(() => {
    if (autoMatchPriceRequestKey.length === 0) {
      setAutoMatchPrices(new Map());
      return;
    }

    let cancelled = false;
    const fetchPrices = async () => {
      // Split into batches the backend will accept. A single oversized request used to
      // be answered with a 200 full of zeros, which then overwrote every correct base
      // value on the page.
      const chunks = chunkPriceRequest(autoMatchPriceRequestKey, CALCULATE_PRICE_BATCH_SIZE);

      // allSettled, not all: one rejected chunk must not discard the others.
      const settled = await Promise.allSettled(
        chunks.map(chunk =>
          calculatePrice({
            cards: chunk.map(k => ({
              wantListId: k.wantListId,
              cardId: k.haveCardId,
              condition: k.condition,
              language: k.language,
              ...finishFields(k.finish),
            }))
          }).unwrap()
        )
      );

      if (cancelled) return;

      setAutoMatchPrices(
        mergeChunkedPrices(chunks, settled, (message, detail) =>
          detail === undefined ? console.error(message) : console.error(message, detail)
        )
      );
    };

    fetchPrices();
    return () => { cancelled = true; };
  }, [autoMatchPriceRequestKey, calculatePrice]);

  // Overlay resolved printing-specific prices onto auto-match expanded card objects
  // This ensures getCardDisplayPrice (which reads card.pointValue) shows correct per-printing prices
  const processedPackagesWithPrices: ProcessedPackage[] = useMemo(() => {
    if (autoMatchPrices.size === 0) return processedPackages;

    return processedPackages.map(processed => {
      if (!processed.isAutoMatchActive) return processed;

      const updatedCards = processed.valuedCards.map(card => {
        const storedCompositeId = (card as any)._autoMatchCompositeId as string | undefined;
        if (!storedCompositeId) return card;

        const resolvedPrice = autoMatchPrices.get(storedCompositeId);
        // A non-positive price is not a resolution. Every rendered row already passed
        // the value > 0 filter above, so a 0 from this endpoint is always wrong for a
        // row that is on screen - keep the base pointValue instead of lowering it.
        // A null slot (the printing has no price) is no resolution either.
        if (!hasRealPrice(resolvedPrice)) return card;

        return { ...card, pointValue: resolvedPrice };
      });

      return { ...processed, valuedCards: updatedCards };
    });
  }, [processedPackages, autoMatchPrices]);

  // Keep the selection handlers' view of the rendered cards current ("latest
  // ref" pattern - handlers only run from user events, after render commits).
  renderedPackagesRef.current = new Map(
    processedPackagesWithPrices.map(processed => [processed.pkg.userId, processed])
  );

  // Reset auto-advance counter when filters change or user manually navigates
  useEffect(() => {
    setAutoAdvanceCount(0);
  }, [debouncedSearchTerm, debouncedPriceMin, debouncedPriceMax, autoMatchServerFilter]);

  // Auto-advance past empty pages when auto-match is on
  // If the current page has 0 visible packages after filtering, skip to the next page
  useEffect(() => {
    if (!globalAutoMatch) return;
    if (isLoading || isFetching || shouldSkipPackagesQuery) return;
    if (!data || data.totalPackages === 0) return;
    if (processedPackagesWithPrices.length > 0) {
      setAutoAdvanceCount(0); // Reset when we find results
      return;
    }
    if (currentPage >= totalPages) return; // No more pages
    if (autoAdvanceCount >= 5) return; // Safety limit to prevent infinite loops

    setAutoAdvanceCount(prev => prev + 1);
    setCurrentPage(prev => prev + 1);
  }, [globalAutoMatch, processedPackagesWithPrices.length, isLoading, isFetching, shouldSkipPackagesQuery, data, currentPage, totalPages, autoAdvanceCount]);

  // Redirect away from empty/out-of-range pages (non-auto-match scenario)
  // Handles: page count reduced by filter changes, URL manipulation, client-side filtering
  useEffect(() => {
    if (isLoading || isFetching || shouldSkipPackagesQuery) return;
    if (!data) return;
    if (globalAutoMatch) return; // auto-match has its own auto-advance logic above

    // Case 1: currentPage exceeds totalPages (e.g., filters reduced results)
    if (currentPage > totalPages && totalPages >= 1) {
      setCurrentPage(totalPages);
      return;
    }

    // Case 2: Current page emptied by client-side filtering (e.g., locked packages)
    if (packagesWithValidAddresses.length === 0 && data.totalPackages > 0 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
      return;
    }
  }, [isLoading, isFetching, shouldSkipPackagesQuery, data, globalAutoMatch, currentPage, totalPages, packagesWithValidAddresses.length]);

  // Loading state - only show on initial load, not when refetching
  if (showLoadingState) {
    return (
      <div className="send-page">
        <SendPageHeader
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
          onSearch={setSearchQuery}
          initialSearchValue={searchQuery}
          searchType={searchType}
          onSearchTypeChange={setSearchType}
          globalAutoMatch={globalAutoMatch}
          onGlobalAutoMatchToggle={handleGlobalAutoMatchToggle}
          isMatchIndexLoading={isMatchIndexLoading}
          hasMatchIndexError={!!matchIndexData?.error}
          onRetryMatchIndex={refetchMatchIndex}
          matchSummary={matchSummary}
          priceMin={priceMin}
          priceMax={priceMax}
          onPriceMinChange={setPriceMin}
          onPriceMaxChange={setPriceMax}
          sortValue={sortValue}
          onSortChange={handleSortChange}
          isSortLoading={isSortLoading}
          hasResults={false}
          selectedSets={selectedSets}
          onSelectedSetsChange={setSelectedSets}
          collapseAll={traderCollapse.collapseAll}
          onCollapseAllToggle={traderCollapse.toggleCollapseAll}
        />
        <CardPrintingsContainer>
          <div className="send-loading-container">
            <div className="send-progress-bar">
              <div className="send-progress-fill send-progress-indeterminate" />
            </div>
            <p className="send-progress-text">
              {loadingMessage}
            </p>
          </div>
        </CardPrintingsContainer>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="send-page">
        <SendPageHeader
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
          onSearch={setSearchQuery}
          initialSearchValue={searchQuery}
          searchType={searchType}
          onSearchTypeChange={setSearchType}
          globalAutoMatch={globalAutoMatch}
          onGlobalAutoMatchToggle={handleGlobalAutoMatchToggle}
          isMatchIndexLoading={isMatchIndexLoading}
          hasMatchIndexError={!!matchIndexData?.error}
          onRetryMatchIndex={refetchMatchIndex}
          matchSummary={matchSummary}
          priceMin={priceMin}
          priceMax={priceMax}
          onPriceMinChange={setPriceMin}
          onPriceMaxChange={setPriceMax}
          sortValue={sortValue}
          onSortChange={handleSortChange}
          isSortLoading={isSortLoading}
          hasResults={false}
          selectedSets={selectedSets}
          onSelectedSetsChange={setSelectedSets}
          collapseAll={traderCollapse.collapseAll}
          onCollapseAllToggle={traderCollapse.toggleCollapseAll}
        />
        <CardPrintingsContainer>
          <div style={{ padding: '20px', textAlign: 'center', color: '#ff6b6b' }}>
            Error loading packages. Please try again later.
          </div>
        </CardPrintingsContainer>
      </div>
    );
  }

  // SECURITY WARNING: This is for DISPLAY ONLY
  // Backend must recalculate all prices to prevent manipulation
  const getCardDisplayPrice = (card: ApiTradeCard): number => {
    // Backend calculates pointValue based on foil status - just use it directly
    return card.pointValue;
  };

  // A package's cards in display order: highest value first, as always. A
  // collapsed package leads with the newest wants instead (the value order then
  // breaks ties), so a new want still surfaces in its two rows.
  const packageCardOrder = (cards: ApiTradeCard[], collapsed: boolean): ApiTradeCard[] => {
    const byValue = [...cards].sort((a, b) => getCardDisplayPrice(b) - getCardDisplayPrice(a));
    return collapsed ? orderForCollapsed(byValue, (card) => card.wantCreatedAt) : byValue;
  };

  // SECURITY WARNING: This total is for DISPLAY ONLY
  // Backend must recalculate to prevent users from manipulating totals
  const calculatePackageTotalValue = (cards: ApiTradeCard[]): number => {
    // WARNING: This can be manipulated in DevTools
    // Backend will calculate real total during trade initiation
    return cards.reduce((sum, card) => {
      const displayPrice = getCardDisplayPrice(card);
      return sum + (displayPrice * card.quantity);
    }, 0);
  };

  return (
    <div className="send-page">
      <h1 className="send-page-title">Send Cards</h1>
      <SendPageHeader
        displayMode={displayMode}
        onDisplayModeChange={setDisplayMode}
        onSearch={setSearchQuery}
        initialSearchValue={searchQuery}
        searchType={searchType}
        onSearchTypeChange={setSearchType}
        globalAutoMatch={globalAutoMatch}
        onGlobalAutoMatchToggle={handleGlobalAutoMatchToggle}
        isMatchIndexLoading={isMatchIndexLoading}
        hasMatchIndexError={!!matchIndexData?.error}
        onRetryMatchIndex={refetchMatchIndex}
        matchSummary={matchSummary}
        priceMin={priceMin}
        priceMax={priceMax}
        onPriceMinChange={setPriceMin}
        onPriceMaxChange={setPriceMax}
        sortValue={sortValue}
        onSortChange={handleSortChange}
        onExportCsv={handleExportCsv}
        isExporting={isExporting}
        hasResults={(data?.totalPackages ?? 0) > 0}
        selectedSets={selectedSets}
        onSelectedSetsChange={setSelectedSets}
        collapseAll={traderCollapse.collapseAll}
        onCollapseAllToggle={traderCollapse.toggleCollapseAll}
      />

      {meData?.preferDomesticTrades && meData?.country && <DomesticTradesBanner />}

      <CardPrintingsContainer>
        {/* Loading overlay for pagination/search transitions */}
        {showPaginationLoading && (
          <div className="send-pagination-loading-overlay">
            <div className="send-progress-bar">
              <div className="send-progress-fill send-progress-indeterminate" />
            </div>
            <p className="send-progress-text">
              {debouncedSearchTerm ? `Searching for "${debouncedSearchTerm}"...` : `Loading page ${currentPage}...`}
            </p>
          </div>
        )}
        {/* Top Pagination — always visible when multiple pages exist */}
        {totalPages > 1 && (
          <div className="send-pagination send-pagination-top">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
              <span className="pagination-total">
                {' '}({globalAutoMatch && matchIndexData?.totalMatchingPackages != null
                  ? `${matchIndexData.totalMatchingPackages} matching users`
                  : `${data?.totalPackages || 0} users`})
              </span>
            </div>
          </div>
        )}
        {packagesWithValidAddresses.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            {searchQuery || priceMin || priceMax ? (
              <>
                <p style={{ margin: 0 }}>
                  No packages found
                  {searchQuery && ` matching "${searchQuery}"`}
                  {(priceMin || priceMax) && ` in the ${priceMin || 0} - ${priceMax || '∞'} point range`}
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setPriceMin(undefined);
                    setPriceMax(undefined);
                  }}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    border: '1px solid #666',
                    borderRadius: '4px',
                    color: '#999',
                    cursor: 'pointer'
                  }}
                >
                  Clear all filters
                </button>
              </>
            ) : (
              'No packages available at the moment. Check back later!'
            )}
          </div>
        ) : processedPackagesWithPrices.length === 0 && globalAutoMatch ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            {autoAdvanceCount >= 5 ? (
              <p>No more matching users found on remaining pages. Try turning off Auto-Match to see all available trades.</p>
            ) : (
              <>
                <span className="loading-spinner-small" />
                <p>Finding matching users...</p>
              </>
            )}
          </div>
        ) : (
          <>
          {displayMode === 'list' ? (
            <SendListView
              packages={processedPackagesWithPrices}
              selectedCardsPerPackage={selectedCardsPerPackage}
              removedPackages={removedPackages}
              onToggleAllCardInstances={handleToggleAllCardInstances}
              onSendPackage={handleSendPackage}
              onRemoveAll={handleRemoveAllCards}
              onReset={handleResetPackage}
              onAutoMatchToggle={handlePackageAutoMatchToggle}
              globalAutoMatch={globalAutoMatch}
              onCardMouseEnter={handleCardMouseEnter}
              onCardMouseLeave={handleCardMouseLeave}
              haveListQuantityByCardIdMap={haveListQuantityByCardIdMap}
              collapse={traderCollapse}
            />
          ) : (
          processedPackagesWithPrices.map(({ pkg, valuedCards, packageKey, isPackageRemoved, isAutoMatchActive, isEmptyDueToAutoMatch, matchingCardCount, totalCardCount, haveMatchMap }) => {
          // Tiles this package renders in the current density: one per copy in
          // Comfortable, one per card in Compact. "Show all N +M more" counts these.
          const tileCount = displayMode === 'comfortable'
            ? valuedCards.reduce((sum, card) => sum + card.quantity, 0)
            : valuedCards.length;
          return (
            <TraderCollapse
              key={pkg.userId}
              traderId={pkg.userId}
              username={pkg.username}
              total={tileCount}
              noun="cards"
              collapse={traderCollapse}
              enabled={!isEmptyDueToAutoMatch}
              layoutKey={displayMode}
            >
              {(c) => (
            <CardGroupContainerResponsive
              headerAction={c.headerToggle}
              title={isAutoMatchActive
                ? `${matchingCardCount} of ${totalCardCount} cards match`
                : `${valuedCards.reduce((sum, card) => sum + card.quantity, 0)} Cards in package`}
              userName={pkg.username}
              userAvatar={cleanImageUrl(pkg.avatarUrl)}
              userCountry={pkg.country || pkg.shippingAddress?.country}
              userPoints={pkg.pointBalance}
              userIsElite={pkg.isElite}
              userShowAnimatedBorder={pkg.showAnimatedBorder}
              userBorderColor1={pkg.borderColor1}
              userBorderColor2={pkg.borderColor2}
              totalValue={calculatePackageTotalValue(valuedCards)}
              autoMatch={isAutoMatchActive}
              mode="send"
              onAutoMatchToggle={(enabled) => handlePackageAutoMatchToggle(pkg.userId, enabled)}
              autoMatchDisabled={globalAutoMatch}
              matchingCardCount={matchingCardCount}
              totalCardCount={totalCardCount}
              onSend={() => handleSendPackage({...pkg, cards: valuedCards})}
              onRemoveAll={() => handleRemoveAllCards(pkg.userId)}
              onReset={() => handleResetPackage(pkg.userId)}
              username={pkg.username}
              isRemoved={isPackageRemoved}
            >
              {/* Empty state when auto-match filters out all cards */}
              {isEmptyDueToAutoMatch ? (
                <div className="auto-match-empty-state">
                  <p className="auto-match-empty-text">
                    No cards from your Have List match this trader's wants
                  </p>
                  <button
                    className="auto-match-view-all-btn"
                    onClick={() => handlePackageAutoMatchToggle(pkg.userId, false)}
                    disabled={globalAutoMatch}
                  >
                    {globalAutoMatch ? 'Turn off global Auto-Match to view all' : 'View all cards'}
                  </button>
                </div>
              ) : (
              // One column: the package body is a flex ROW, so the grid and the
              // button under it need a wrapper or they would sit side by side.
              <div className="trader-collapse-stack">
              <div className="card-grid" ref={c.gridRef}>
                {displayMode === 'comfortable' ? (
                  // Comfortable mode: Show individual card images for each quantity
                  c.limit(packageCardOrder(valuedCards, c.collapsed).flatMap((card) => {
                  // Display the appropriate price: prefer nonfoil if available, otherwise use foil
                  const displayPrice = getCardDisplayPrice(card);

                  return Array.from({ length: card.quantity }, (_, index) => {
                    const cardKey = sendCardCopyKey(card, index);
                    const selectedCards = selectedCardsPerPackage.get(packageKey);

                    // Determine if card is selected
                    let isSelected = true;

                    if (isPackageRemoved) {
                      // If package is removed, all cards are deselected
                      isSelected = false;
                    } else if (selectedCards !== undefined) {
                      // If we have interacted with this package, use the selection set
                      // (even if it's empty - empty means nothing selected)
                      isSelected = selectedCards.has(cardKey);
                    }
                    // Otherwise, no interaction yet - default to all selected (true)

                    return (
                      <div
                        key={cardKey}
                        className="card-item"
                        onClick={() => handleToggleCard(pkg.userId, cardKey)}
                        onMouseEnter={(e) => handleCardMouseEnter(e, card, pkg.username)}
                        onMouseLeave={handleCardMouseLeave}
                        style={{ cursor: 'pointer', position: 'relative' }}
                      >
                        <SendViewCardLink card={card} />
                        {c.collapsed && isNewWant(card.wantCreatedAt) && <NewWantTag />}
                        <FoilCardImage
                          className="card-item-image"
                          src={(isAutoMatchActive ? getAutoMatchImageUrl(card, haveMatchMap) : undefined) || card.imageUrl || PLACEHOLDER_IMAGE}
                          alt={card.name}
                          finish={finishFromRow(card)}
                          borderRadius="8px"
                          onError={(e) => handleImageError(e, card)}
                          loading="lazy"
                        />
                        {(failedImages.has(card.id) || !card.imageUrl) && (
                          <div className="card-name-overlay">{card.name}</div>
                        )}
                        <div className="card-item-points">
                          <Points points={displayPrice} type="small" />
                        </div>
                        {!isSelected && (
                          <div className="deselected-overlay">
                            <img
                              className="heart-icon broken"
                              src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg"
                              alt="Deselected"
                            />
                            <img
                              className="heart-icon unbroken"
                              src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/heart.svg"
                              alt="Click to select"
                            />
                          </div>
                        )}
                      </div>
                    );
                  });
                }))
              ) : (
                // Compact mode: Show one card image with quantity badge
                c.limit(packageCardOrder(valuedCards, c.collapsed).map((card) => {
                  // Display the appropriate price: prefer nonfoil if available, otherwise use foil
                  const displayPrice = getCardDisplayPrice(card);

                  // Check if all instances of this card are deselected
                  const selectedCards = selectedCardsPerPackage.get(packageKey);
                  let selectedCount = 0;

                  if (isPackageRemoved) {
                    // All cards are deselected
                    selectedCount = 0;
                  } else if (selectedCards === undefined) {
                    // No interaction yet - default to all cards selected
                    selectedCount = card.quantity;
                  } else {
                    // We have interacted - count selected cards (even if set is empty)
                    for (let i = 0; i < card.quantity; i++) {
                      if (selectedCards.has(sendCardCopyKey(card, i))) {
                        selectedCount++;
                      }
                    }
                  }
                  const isFullyDeselected = selectedCount === 0;

                  return (
                    <div
                      key={(card as any)._autoMatchCompositeId || card.id}
                      className="card-item"
                      onClick={() => handleToggleAllCardInstances(pkg.userId, card)}
                      onMouseEnter={(e) => handleCardMouseEnter(e, card, pkg.username)}
                      onMouseLeave={handleCardMouseLeave}
                      style={{ cursor: 'pointer', position: 'relative' }}
                    >
                      <SendViewCardLink card={card} />
                      {c.collapsed && isNewWant(card.wantCreatedAt) && <NewWantTag />}
                      <div className="card-quantity-badge">{selectedCount > 0 ? selectedCount : card.quantity}</div>
                      <FoilCardImage
                        className="card-item-image"
                        src={(isAutoMatchActive ? getAutoMatchImageUrl(card, haveMatchMap) : undefined) || card.imageUrl || PLACEHOLDER_IMAGE}
                        alt={card.name}
                        finish={finishFromRow(card)}
                        borderRadius="8px"
                        onError={(e) => handleImageError(e, card)}
                        loading="lazy"
                      />
                      {(failedImages.has(card.id) || !card.imageUrl) && (
                        <div className="card-name-overlay">{card.name}</div>
                      )}
                      <div className="card-item-points">
                        <Points points={displayPrice} type="small" />
                      </div>
                      {isFullyDeselected && (
                        <div className="deselected-overlay" style={{ opacity: 0.7 }}>
                          <img
                            className="heart-icon"
                            src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg"
                            alt="All deselected"
                            style={{ width: 30, height: 30 }}
                          />
                        </div>
                      )}
                    </div>
                  );
                }))
              )}
            </div>
              {c.footer}
              </div>
              )}
          </CardGroupContainerResponsive>
              )}
            </TraderCollapse>
          );
        })
          )}
          </>
        )}
        {/* Global auto-match empty state when all packages are filtered out */}
        {globalAutoMatch && matchSummary?.matchingPackages === 0 && (
          <div className="auto-match-global-empty-state">
            <p>No matching packages found.</p>
            <p>Turn off Auto-Match to see all available trades.</p>
          </div>
        )}
        {/* Bottom Pagination — always visible when multiple pages exist */}
        {totalPages > 1 && (
          <div className="send-pagination">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
              <span className="pagination-total">
                {' '}({globalAutoMatch && matchIndexData?.totalMatchingPackages != null
                  ? `${matchIndexData.totalMatchingPackages} matching users`
                  : `${data?.totalPackages || 0} users`})
              </span>
            </div>
          </div>
        )}
      </CardPrintingsContainer>

      <InitiateTradeModal
        isOpen={isModalOpen}
        cards={getCurrentCards()}
        onClose={handleCloseModal}
        onInitiateTrade={handleInitiateTrade}
        isLoading={isInitiatingTrade}
        lockId={currentLockId || undefined}
        lockExpiresAt={lockExpiresAt || undefined}
        receiverUsername={selectedPackage?.username}
        receiverAvatarUrl={cleanImageUrl(selectedPackage?.avatarUrl)}
        receiverId={selectedPackage?.userId}
        receiverPoints={selectedPackage?.pointBalance}
        receiverCountry={selectedPackage?.country || selectedPackage?.shippingAddress?.country}
        haveListQuantityByCardIdMap={haveListQuantityByCardIdMap}
      />

      <CommitmentConflictDialog
        isOpen={commitmentConflicts.length > 0}
        conflicts={commitmentConflicts}
        isCancellingOffer={isCancellingOffer}
        onAbort={clearCommitmentConflicts}
        onCancelConflictingOffers={async () => {
          if (await cancelConflictingOffers() && pendingTradeCardsRef.current) {
            handleInitiateTrade(pendingTradeCardsRef.current);
          }
        }}
        onEditHaveList={() => {
          clearCommitmentConflicts();
          navigate('/my-cards/have-list');
        }}
      />

      {/* Card hover tooltip */}
      {hoveredCardData && (
        <SendCardTooltip
          cardData={hoveredCardData.data}
          position={hoveredCardData.position}
          username={hoveredCardData.username}
          onClose={handleCardMouseLeave}
          onSetHover={setHoveredSetCode}
          hoveredSetCode={hoveredSetCode}
          showImage={displayMode === 'list'}
        />
      )}
    </div>
  );
}
